
import { useState } from "react";
import { Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatRelative } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";

interface OpportunityNotesProps {
  opportunityId: string;
  notes: any[];
  onAddNote: (data: { opportunity_id: string; content: string; user_id?: string }) => void;
  isLoading?: boolean;
}

export function OpportunityNotes({ 
  opportunityId, 
  notes, 
  onAddNote, 
  isLoading = false 
}: OpportunityNotesProps) {
  const [noteContent, setNoteContent] = useState("");
  const { user } = useAuth();

  const handleAddNote = () => {
    if (!noteContent.trim()) return;
    
    onAddNote({ 
      opportunity_id: opportunityId, 
      content: noteContent,
      user_id: user?.id
    });
    
    setNoteContent("");
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    return formatRelative(new Date(dateString), new Date());
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">Notes</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Textarea 
              placeholder="Add a note..."
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              className="min-h-[80px] flex-1"
            />
            <Button 
              className="self-end" 
              onClick={handleAddNote}
              disabled={!noteContent.trim() || isLoading}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          
          {isLoading ? (
            <div className="py-4 text-center text-muted-foreground">Loading notes...</div>
          ) : notes.length === 0 ? (
            <div className="py-4 text-center text-muted-foreground">No notes yet</div>
          ) : (
            <div className="space-y-4 mt-4">
              {notes.map((note) => (
                <div key={note.id} className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={note.user?.avatar_url} />
                    <AvatarFallback>
                      {note.user ? 
                        getInitials(note.user.first_name, note.user.last_name) : 
                        "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="bg-muted p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <div className="font-medium text-sm">
                          {note.user ? 
                            `${note.user.first_name} ${note.user.last_name}` : 
                            "Unknown User"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(note.created_at)}
                        </div>
                      </div>
                      <div className="text-sm">{note.content}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
